<script lang="ts" setup>
import {
  onUnmounted, reactive, ref,
} from 'vue';
import {
  Button, Form, FormItem, Spin, Input,
} from 'ant-design-vue';
import type { Rule } from 'ant-design-vue/es/form';
import type { FormInstance } from 'ant-design-vue';
import { PhoneOutlined } from '@ant-design/icons-vue';
import type { ApiResponse} from 'use-api-vue';
import { usePostRef } from 'use-api-vue';

import type { ServerErrors } from '@/util/api/ajax/ApiResponse';

interface PhoneForm {
  phone: string;
}

const formState = reactive<PhoneForm>({
  phone: '',
});
const formRef = ref<FormInstance>();

const isValidPhone = (phone: string): boolean => {
  const strippedPhone = phone.replace(/[^0-9]/g, '');
  if (strippedPhone.length !== 10) {
    return false;
  }
  return true;
};

const phoneValidator = async (_rule: Rule, value: string) => {
  // naive phone validation, @todo replace this with something robust
  if (!isValidPhone(value)) {
    return Promise.reject(new Error('Phone number must be 10 digits.'));
  }
  return Promise.resolve();
};

const rules: Record<string, Rule[]> = {
  phone: [{ required: true, validator: phoneValidator }],
};

const { loading, send } = usePostRef<ApiResponse<unknown>, unknown, ServerErrors>(
  { onCancelCallback: onUnmounted },
);

const emit = defineEmits(['formFinish']);

function formFinish() {
  emit('formFinish');
}
const onSubmit = async () => {
  const response = await send('mfa/updateSmsPhone', formState);
  if (response instanceof Error) {
    throw Error('Unable to reach server.');
  }
  formFinish();
};

const isButtonDisabled = () => !isValidPhone(formState.phone);
</script>

<template>
  <div>
    <p>
      Enter your phone number so we can send you a verification text message.
    </p>
  </div>
  <Spin :spinning="loading">
    <Form
      ref="formRef"
      :rules="rules"
      :model="formState"
      @submit="onSubmit"
    >
      <FormItem has-feedback name="phone">
        <Input v-model:value="formState.phone">
          <template #suffix>
            <PhoneOutlined />
          </template>
        </Input>
      </FormItem>
      <Button
        type="primary"
        block
        html-type="submit"
        :disabled="isButtonDisabled()"
      >
        Next
      </Button>
    </Form>
  </Spin>
</template>
